import { useAPI } from '@/composables';
import type {
  PaymentAccountResponse,
  PaymentPlansResponse,
  PaymentSubscriptionInitiateResponse
} from '@/types/payment-types';
import { params2Query } from '@/utils';

/**
 * Get all available plans
 *
 * @param params Optional parameters (e.g., gateway)
 * @returns Promise with plans data
 */
export const getPlans = (params: Record<string, any> = {}) => {
  return useAPI<PaymentPlansResponse>(
    `/payments/plans?${params2Query(params)}`
  );
};

/**
 * Initiate a subscription to a plan
 *
 * @param planUuid The UUID of the plan to subscribe to
 * @param entity The entity type (e.g., 'vendor')
 * @param gateway Optional payment gateway to use
 * @returns Promise with subscription initiation data
 */
export const initiateSubscription = (
  planUuid: string,
  entity: string = 'vendor',
  gateway: string | null = null
) => {
  const url = gateway
    ? `/payments/subscription/subscribe/${planUuid}/${entity}/${gateway}`
    : `/payments/subscription/subscribe/${planUuid}/${entity}`;
  return useAPI<PaymentSubscriptionInitiateResponse>(url);
};

/**
 * Cancel an existing subscription
 *
 * @param subscriptionId The ID of the subscription to cancel
 * @param gateway Optional payment gateway to use
 * @returns Promise with cancellation result
 */
export const cancelSubscription = (
  subscriptionId: string,
  gateway: string | null = null
) => {
  const baseUrl = gateway
    ? `/payments/subscription/weekbook/cancel/${gateway}`
    : `/payments/subscription/weekbook/cancel`;
  return useAPI(baseUrl, {
    method: 'POST',
    body: { id: subscriptionId }
  });
};

/**
 * Get the payment account information
 *
 * @param gateway Optional payment gateway to use
 * @returns Promise with account information
 */
export const getPaymentAccount = (gateway: string | null = null) => {
  const url = gateway
    ? `/payments/subscription/account/${gateway}`
    : `/payments/subscription/account`;
  return useAPI<PaymentAccountResponse>(url);
};

// Define the interfaces needed for purchase
export interface PurchaseItem {
  purchasable_id: number;
  quantity?: number;
  metadata?: Record<string, any>;
}

export interface PurchaseRequest {
  items: PurchaseItem[];
  entity: string;
  payment_data?: Record<string, any>;
}

export interface PurchaseResponse {
  status: boolean;
  message?: string;
  order_id?: number;
  gateway?: string;
}

/**
 * Process a purchase of items
 *
 * @param request The purchase request data
 * @param gateway Optional payment gateway to use
 * @returns Promise with purchase result
 */
export const processPurchase = (
  request: PurchaseRequest,
  gateway: string | null = null
) => {
  const url = gateway
    ? `/payments/subscription/purchase/${gateway}`
    : `/payments/subscription/purchase`;

  return useAPI<PurchaseResponse>(url, {
    method: 'POST',
    body: request,
    cache: 'no-store'
  });
};
